import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, CloseIcon, cn } from "@babybjorn/ui";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			"bg-gray-4/25 fixed inset-0 z-50 cursor-pointer",
			"transition-animation duration-200",
			"data-[state=open]:animate-in data-[state=open]:fade-in",
			"data-[state=closed]:animate-out data-[state=closed]:fade-out",
			className,
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<DialogPortal>
		<DialogOverlay />
		<DialogPrimitive.Content
			ref={ref}
			className={cn(
				"fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden",
				"bg-gray-0 z-50 grid w-full max-w-128",
				"shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1)]",
				"transition-all duration-200",
				"[@media(min-width:640px)]:rounded-md",
				"data-[state=open]:animate-in data-[state=open]:zoom-in-95 data-[state=open]:fade-in",
				"data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out",
				className,
			)}
			{...props}
		>
			{children}
			<Button
				variant="secondary"
				size="icon-tiny"
				asChild
				className={cn("top-bb right-bb absolute")}
			>
				<DialogPrimitive.Close>
					<CloseIcon />
					<span className="sr-only">Close</span>
				</DialogPrimitive.Close>
			</Button>
		</DialogPrimitive.Content>
	</DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col gap-[0.375rem] text-center",
			"@media(min-width:640px):text-left",
			className,
		)}
		{...props}
	/>
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col-reverse gap-2",
			"@media(min-width:640px):flex-row @media(min-width:640px):justify-end",
			className,
		)}
		{...props}
	/>
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			"text-lg leading-none font-semibold tracking-tight",
			className,
		)}
		{...props}
	/>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn("text-gray-5 text-sm", className)}
		{...props}
	/>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
	Dialog,
	DialogPortal,
	DialogOverlay,
	DialogTrigger,
	DialogClose,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
};
